import type { Component } from 'vue';
import { defineAsyncComponent } from 'vue';

/**
 * Registry mapping a model-chosen component `type` onto a renderer.
 *
 * Design constraints (Paul, phase 2):
 *  - The MODEL decides what to display; there is no automatic tool->card mapping.
 *  - The model sends ONLY the type and identifying ids — never record data.
 *    Each renderer therefore fetches its own data under the signed-in user's
 *    permissions, so a card can never surface something the user can't see.
 *  - Renderers reuse components that already exist in the app wherever possible.
 *  - Cards carry no model-driven action layer: whatever native actions the
 *    underlying component already has are the only actions available.
 *
 * Unknown types degrade to a small "can't display this yet" card — an
 * unrecognised type must never break the page.
 */

export interface AiCardDefinition {
  /** Lazily-loaded renderer; keeps heavy views out of the main bundle. */
  component: Component;
  /** Narrow the model's raw props down to what the renderer accepts. */
  propsMapper: (props: Record<string, unknown>) => Record<string, unknown>;
  /** Shown when the model didn't supply a title. */
  titleFallback: string;
}

/** Read an id-ish prop that may arrive as a string or a number. */
export function readIdProp(props: Record<string, unknown>, ...names: string[]): string | undefined {
  for (const name of names) {
    const value = props[name];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

export const AI_CARD_REGISTRY: Record<string, AiCardDefinition> = {
  // Reuses src/components/common/StudentDetailsCard.vue (avatar, name, matric,
  // class, contact) behind a thin fetcher — that component is purely
  // presentational and takes a `student` object, so the wrapper supplies it.
  student_card: {
    component: defineAsyncComponent(() => import('./AiStudentCard.vue')),
    propsMapper: (props) => ({ studentId: readIdProp(props, 'studentId', 'student_id', 'id') }),
    titleFallback: 'Student',
  },

  // Reuses the existing billing data layer: useStudentFinancialSummary for
  // the tiles and useStudentLedger for the detailed transaction breakdown
  // (src/composables/billing/useStudentFinancialProfile.ts), the same
  // id-only composables StudentBillingPeekDrawer and the student financial
  // profile page already call.
  billing_summary: {
    component: defineAsyncComponent(() => import('./AiBillingSummaryCard.vue')),
    propsMapper: (props) => ({
      studentId: readIdProp(props, 'studentId', 'student_id', 'id'),
      sessionId: readIdProp(props, 'sessionId', 'session_id'),
      academicCalendarId: readIdProp(props, 'academicCalendarId', 'academic_calendar_id', 'calendarId'),
    }),
    titleFallback: 'Billing summary',
  },

  // Reuses src/components/DashBoard/Student/Overview/TertiaryStudentResult.vue
  // as-is — it already takes just a studentId, fetches its own result data and
  // picks its own calendar, with no page-context dependencies.
  results_summary: {
    component: defineAsyncComponent(() => import('./AiResultsSummaryCard.vue')),
    propsMapper: (props) => ({
      studentId: readIdProp(props, 'studentId', 'student_id', 'id'),
      calendarId: readIdProp(props, 'calendarId', 'academic_calendar_id', 'academicCalendarId'),
    }),
    titleFallback: 'Results summary',
  },

  // Embeds the form's own public page. Created by the `open_form_preview`
  // action, which resolves the public URL from the form id first.
  form_preview: {
    component: defineAsyncComponent(() => import('./AiFormPreviewCard.vue')),
    propsMapper: (props) => ({
      url: typeof props.url === 'string' ? props.url : undefined,
      formId: readIdProp(props, 'formId', 'form_id'),
    }),
    titleFallback: 'Form preview',
  },

  // The real form builder (FormBuilderView in embedded mode) opened on one
  // form, so forms can be managed from the workspace directly. The wrapper
  // first checks the form resolves and has a field schema.
  form_edit: {
    component: defineAsyncComponent(() => import('./AiFormEditCard.vue')),
    propsMapper: (props) => ({ formId: readIdProp(props, 'formId', 'form_id', 'id') }),
    titleFallback: 'Form builder',
  },

  // What one class is being taught. Tertiary: the level's course curriculum in
  // the Courses page's grouped view (read-only); other schools: the classroom's
  // schemes of work.
  curriculum: {
    component: defineAsyncComponent(() => import('./AiCurriculumCard.vue')),
    propsMapper: (props) => ({
      classroomSectionId: readIdProp(props, 'classroomSectionId', 'classroom_section_id', 'sectionId', 'id'),
    }),
    titleFallback: 'Curriculum',
  },

  // Reuses the student profile's course-registrations panel (calendar filter,
  // unit totals, permission-gated add/approve/delete) behind an id fetch.
  student_course_registration: {
    component: defineAsyncComponent(() => import('./AiStudentCourseRegistrationCard.vue')),
    propsMapper: (props) => ({ studentId: readIdProp(props, 'studentId', 'student_id', 'id') }),
    titleFallback: 'Course registration',
  },

  // K12: the student's subject list (read-only — enrolment is edited on the
  // profile page). Tertiary: delegates to the course-registration renderer
  // with an explicit school-type note.
  student_subject_enrollment: {
    component: defineAsyncComponent(() => import('./AiStudentSubjectEnrollmentCard.vue')),
    propsMapper: (props) => ({ studentId: readIdProp(props, 'studentId', 'student_id', 'id') }),
    titleFallback: 'Subject enrolment',
  },

  // Reuses the generated listCBTTests hook the admin CBT screens use,
  // narrowed server-side to the resolved class arm via filter.section_id.
  cbt_tests: {
    component: defineAsyncComponent(() => import('./AiCbtTestsCard.vue')),
    propsMapper: (props) => ({
      classroomSectionId: readIdProp(props, 'classroomSectionId', 'classroom_section_id', 'sectionId', 'id'),
    }),
    titleFallback: 'CBT tests',
  },

  // Reuses useStudentCBTTestSessions (the student CBT report data layer)
  // with a server-side studentId filter.
  cbt_results: {
    component: defineAsyncComponent(() => import('./AiCbtResultsCard.vue')),
    propsMapper: (props) => ({
      studentId: readIdProp(props, 'studentId', 'student_id', 'id'),
    }),
    titleFallback: 'CBT results',
  },

  // Reuses the shared read-only TimetableGrid. Tertiary reads the Level
  // (classroom) timetable; K12 tries the section then falls back to the class.
  timetable: {
    component: defineAsyncComponent(() => import('./AiTimetableCard.vue')),
    propsMapper: (props) => ({
      classroomSectionId: readIdProp(props, 'classroomSectionId', 'classroom_section_id', 'sectionId', 'id'),
    }),
    titleFallback: 'Timetable',
  },

  // The class broadsheet for the active term. K12 is classroom-scoped;
  // tertiary is (programme, level)-scoped with an in-card programme picker.
  results_report: {
    component: defineAsyncComponent(() => import('./AiResultsReportCard.vue')),
    propsMapper: (props) => ({
      classroomSectionId: readIdProp(props, 'classroomSectionId', 'classroom_section_id', 'sectionId', 'id'),
    }),
    titleFallback: 'Results report',
  },

  // Current-term summary tiles + latest daily records (read-only; marking
  // lives on the daily attendance screen the card's action opens).
  student_attendance: {
    component: defineAsyncComponent(() => import('./AiStudentAttendanceCard.vue')),
    propsMapper: (props) => ({ studentId: readIdProp(props, 'studentId', 'student_id', 'id') }),
    titleFallback: 'Attendance',
  },

  // Per-student totals for one class this term; section-keyed on both school
  // types (a tertiary "class" is a level section).
  class_attendance: {
    component: defineAsyncComponent(() => import('./AiClassAttendanceCard.vue')),
    propsMapper: (props) => ({
      classroomSectionId: readIdProp(props, 'classroomSectionId', 'classroom_section_id', 'sectionId', 'id'),
    }),
    titleFallback: 'Class attendance',
  },

  // Pipeline counts + the latest entries for one form; review stays on the
  // submissions screen.
  form_submissions: {
    component: defineAsyncComponent(() => import('./AiFormSubmissionsCard.vue')),
    propsMapper: (props) => ({ formId: readIdProp(props, 'formId', 'form_id', 'id') }),
    titleFallback: 'Form submissions',
  },

  // Same composition as the teacher-details page (profile + subjects, plus
  // course allocations on tertiary). The id is the school_staff row id.
  staff_card: {
    component: defineAsyncComponent(() => import('./AiStaffCard.vue')),
    propsMapper: (props) => ({ staffId: readIdProp(props, 'staffId', 'staff_id', 'teacherId', 'id') }),
    titleFallback: 'Staff member',
  },

  // School-wide: the backend resolves the reference to the school itself, so
  // the card carries no record id and fetches the active session's debtors.
  debtors_list: {
    component: defineAsyncComponent(() => import('./AiDebtorsCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Debtors',
  },

  // ── Billing workspace (school-wide unless noted) ─────────────────────────
  // Same data layers as the billing screens: useBillingKpis, useBills,
  // useBillItemTemplates, usePayments, the relief composables. The cards
  // display; every money-moving decision (publish, record, approve) is a
  // card ACTION the user clicks — see aiCardActions.ts.

  billing_kpis: {
    component: defineAsyncComponent(() => import('./AiBillingKpisCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Billing overview',
  },

  bills_list: {
    component: defineAsyncComponent(() => import('./AiBillsListCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Bills',
  },

  // The one billing card keyed to a record: the backend resolves an invoice
  // number or student reference to the bill id.
  bill_detail: {
    component: defineAsyncComponent(() => import('./AiBillDetailCard.vue')),
    propsMapper: (props) => ({ billId: readIdProp(props, 'billId', 'bill_id', 'id') }),
    titleFallback: 'Bill',
  },

  fee_catalogue: {
    component: defineAsyncComponent(() => import('./AiFeeCatalogueCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Fee catalogue',
  },

  // The Settings → Custom Fields screen itself (#574), shown beside the
  // custom-field tools so the user watches the fields change.
  custom_fields_settings: {
    component: defineAsyncComponent(() => import('./AiCustomFieldsCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Custom fields',
  },

  billing_relief: {
    component: defineAsyncComponent(() => import('./AiBillingReliefCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Discounts & relief',
  },

  billing_report: {
    component: defineAsyncComponent(() => import('./AiBillingReportCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Billing report',
  },

  recent_payments: {
    component: defineAsyncComponent(() => import('./AiPaymentsCard.vue')),
    propsMapper: () => ({}),
    titleFallback: 'Recent payments',
  },

  // ── Workspace actions (system types: only framework tools emit these) ───
  // The assistant proposes, the person confirms. Ids are ULID strings.

  // The server's stored preview of a proposed change, with Confirm/Cancel;
  // after confirm it shows the outcome or the bulk run inline.
  action_confirmation: {
    component: defineAsyncComponent(() => import('./AiActionConfirmationCard.vue')),
    propsMapper: (props) => ({
      pendingActionId: readIdProp(props, 'pendingActionId', 'pending_action_id', 'id'),
    }),
    titleFallback: 'Confirm a change',
  },

  // A frozen set of records a query tool found; bulk actions run over it.
  ai_selection: {
    component: defineAsyncComponent(() => import('./AiSelectionCard.vue')),
    propsMapper: (props) => ({ selectionId: readIdProp(props, 'selectionId', 'selection_id', 'id') }),
    titleFallback: 'Records found',
  },

  // One bulk run, live.
  action_run: {
    component: defineAsyncComponent(() => import('./AiActionRunCard.vue')),
    propsMapper: (props) => ({ runId: readIdProp(props, 'runId', 'run_id', 'id') }),
    titleFallback: 'Bulk run',
  },
};

export function getAiCardDefinition(type: string): AiCardDefinition | null {
  return AI_CARD_REGISTRY[type] ?? null;
}

export function isKnownAiCardType(type: string): boolean {
  return Object.prototype.hasOwnProperty.call(AI_CARD_REGISTRY, type);
}

/**
 * Types the backend declares but which have no frontend renderer yet. Listed so
 * the fallback card can say "not supported yet" rather than "unknown", and so
 * adding a renderer is a one-line registry change.
 *
 * Currently empty — every backend-declared type has a renderer. Keep the list
 * (and the fallback wording it drives) for the next declared-ahead type.
 */
export const DECLARED_FUTURE_AI_CARD_TYPES: readonly string[] = [];
